/**
 * UIManager - Manages all UI panels and widgets
 * Replaces ModalGUI from Java version
 *
 * Layout (Figma-style):
 * - Compact floating bottom dock (#bottom-toolbar) holding the simplified
 *   toolbar (ToolsPanel) + the Design | Make mode toggle
 * - Right floating properties panel (#props-panel). Its content depends on
 *   the app mode:
 *     Design mode: collapsible sections Camera, Physics, Layers, Slices.
 *       Shown only while a design exists (a chair is drawn).
 *     Make mode: the MaterialsPanel (fabrication settings - Cut / 3D Print,
 *       material, exports). Always shown while in Make mode. The panel
 *       element being connected to the DOM is what gates the MaterialsPanel
 *       split-screen cut-pattern overlay on the viewport.
 */

import { ToolsPanel } from './panels/ToolsPanel.js';
import { LayersPanel } from './panels/LayersPanel.js';
import { SlicesPanel } from './panels/SlicesPanel.js';
import { CameraPanel } from './panels/CameraPanel.js';
import { PreviewPanel } from './panels/PreviewPanel.js';
import { MaterialsPanel } from './panels/MaterialsPanel.js';
import { PhysicsPanel } from './panels/PhysicsPanel.js';
import { StrokeModifierPanel } from './panels/StrokeModifierPanel.js';
import { SceneSettingsPanel } from './panels/SceneSettingsPanel.js';
import { ICONS, phIcon } from './icons.js';

export class UIManager {
    constructor(application) {
        this.app = application;

        // UI panels
        this.panels = {};

        // App mode: 'design' (sketching, properties sections) or
        // 'make' (fabrication: split-screen cut overlay + Make settings)
        this.mode = 'design';

        // Properties-panel section open/closed state (in-memory)
        // Properties panel hidden BY THE USER (chevron tab). Overrides the
        // auto-show logic until the user re-shows it. In-memory only.
        this.userHiddenProps = false;

        this.sectionOpen = {
            camera: true,
            physics: true,
            layers: true,
            slices: true,
            crossCurve: true
        };
        this.sections = {};

        // Create UI structure
        this.createUIStructure();

        // Initialize panels
        this.initializePanels();
    }

    /**
     * Create the main UI structure (DOM elements)
     */
    createUIStructure() {
        // Create container for all UI panels
        this.uiContainer = document.createElement('div');
        this.uiContainer.id = 'ui-container';
        this.uiContainer.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            pointer-events: none;
            z-index: 1000;
        `;
        document.body.appendChild(this.uiContainer);

        // Compact floating dock, horizontally centered at the bottom.
        // Width hugs the toolbar content (no fixed width any more).
        this.bottomToolbar = document.createElement('div');
        this.bottomToolbar.id = 'bottom-toolbar';
        this.bottomToolbar.style.cssText = `
            position: absolute;
            left: 50%;
            bottom: 16px;
            transform: translateX(-50%);
            display: flex;
            flex-direction: column;
            max-width: 92vw;
            background: var(--surface);
            backdrop-filter: blur(18px) saturate(1.4);
            -webkit-backdrop-filter: blur(18px) saturate(1.4);
            border-radius: var(--radius-lg);
            border: 1px solid rgba(0, 0, 0, 0.08);
            box-shadow: var(--shadow-dock);
            pointer-events: auto;
        `;
        this.uiContainer.appendChild(this.bottomToolbar);

        // Right floating properties panel
        this.propsPanel = document.createElement('div');
        this.propsPanel.id = 'props-panel';
        this.propsPanel.style.cssText = `
            position: absolute;
            right: 16px;
            top: 72px;
            width: 264px;
            max-height: calc(100vh - 72px - 88px);
            display: none;
            flex-direction: column;
            overflow-y: auto;
            overflow-x: hidden;
            background: var(--surface);
            backdrop-filter: blur(18px) saturate(1.4);
            -webkit-backdrop-filter: blur(18px) saturate(1.4);
            border-radius: var(--radius-lg);
            border: 1px solid rgba(0, 0, 0, 0.08);
            box-shadow: var(--shadow-dock);
            pointer-events: auto;
        `;
        this.uiContainer.appendChild(this.propsPanel);

        // Design-mode content: the collapsible property sections
        this.designContainer = document.createElement('div');
        this.designContainer.id = 'props-design-content';
        this.designContainer.style.cssText = `
            display: flex;
            flex-direction: column;
        `;
        this.propsPanel.appendChild(this.designContainer);

        // Make-mode content: hosts the MaterialsPanel element while the
        // Make mode is active (attached/detached, not just hidden, so the
        // panel's isConnected checks keep gating the split-screen overlay)
        this.makeContainer = document.createElement('div');
        this.makeContainer.id = 'props-make-content';
        this.makeContainer.style.cssText = `
            display: none;
            flex-direction: column;
            padding: 14px;
        `;
        this.propsPanel.appendChild(this.makeContainer);

        // Hide/show chevron tab attached to the panel's left edge (stays
        // visible when the panel is hidden so it can be pulled back out)
        this.propsToggleTab = document.createElement('button');
        this.propsToggleTab.id = 'props-toggle-tab';
        this.propsToggleTab.title = 'Hide panel';
        this.propsToggleTab.style.cssText = `
            position: absolute;
            top: 84px;
            width: 22px;
            height: 46px;
            display: none;
            align-items: center;
            justify-content: center;
            padding: 0;
            border: 1px solid rgba(0, 0, 0, 0.08);
            border-right: none;
            border-radius: 8px 0 0 8px;
            background: var(--surface);
            box-shadow: var(--shadow-dock);
            color: #777;
            cursor: pointer;
            pointer-events: auto;
            z-index: 5;
        `;
        this.propsToggleTab.addEventListener('click', () => {
            this.userHiddenProps = !this.userHiddenProps;
            this.updateLeftPanelVisibility();
        });
        this.uiContainer.appendChild(this.propsToggleTab);

        // Design tokens + shared control styles (sliders, number inputs,
        // toolbar buttons, properties-panel layout)
        const style = document.createElement('style');
        style.textContent = `
            :root {
                --accent: #00B8CF;
                --accent-strong: #0097AC;
                --accent-tint: rgba(0, 184, 207, 0.12);
                --accent-tint-strong: rgba(0, 184, 207, 0.22);
                --surface: rgba(255, 255, 255, 0.86);
                --text: #333;
                --text-dim: #777;
                --radius-md: 10px;
                --radius-lg: 18px;
                --shadow-dock: 0 12px 40px rgba(0, 0, 0, 0.18), 0 2px 8px rgba(0, 0, 0, 0.08);
                --shadow-soft: 0 2px 8px rgba(0, 0, 0, 0.08);
            }
            /* The floating tool settings card (#tool-settings-card) lives
               in #canvas-container, OUTSIDE #ui-container - it must be
               covered too or its sliders fall back to native styling */
            #ui-container input[type="range"],
            #tool-settings-card input[type="range"] {
                -webkit-appearance: none;
                appearance: none;
                height: 4px;
                border-radius: 2px;
                background: #ddd;
                outline: none;
                cursor: pointer;
            }
            #ui-container input[type="range"]::-webkit-slider-thumb,
            #tool-settings-card input[type="range"]::-webkit-slider-thumb {
                -webkit-appearance: none;
                appearance: none;
                width: 14px;
                height: 14px;
                border-radius: 50%;
                background: var(--accent);
                border: 2px solid #fff;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
                cursor: pointer;
            }
            #ui-container input[type="range"]::-moz-range-thumb,
            #tool-settings-card input[type="range"]::-moz-range-thumb {
                width: 14px;
                height: 14px;
                border-radius: 50%;
                background: var(--accent);
                border: 2px solid #fff;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
                cursor: pointer;
            }
            #ui-container input[type="number"] {
                background: #fff;
                border: 1px solid #ccc;
                border-radius: 6px;
                color: var(--text);
            }

            /* --- Main toolbar (Phosphor icon buttons) --- */
            .ph-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 36px;
                height: 36px;
                padding: 0;
                border: none;
                border-radius: 8px;
                background: transparent;
                color: #444;
                cursor: pointer;
                flex-shrink: 0;
                transition: background 0.15s ease, color 0.15s ease;
            }
            .ph-btn:hover {
                background: rgba(0, 0, 0, 0.05);
            }
            .ph-btn.active {
                background: var(--accent-tint);
                color: var(--accent-strong);
            }
            .ph-btn.ph-chevron {
                width: 16px;
                height: 36px;
                margin-left: -2px;
                color: #888;
            }

            .sc-tool-btn {
                transition: background 0.15s ease, color 0.15s, border-color 0.15s, transform 0.1s;
            }
            .sc-tool-btn:hover {
                transform: translateY(-1px);
            }

            /* --- Right properties panel --- */
            .props-section {
                border-bottom: 1px solid rgba(0, 0, 0, 0.06);
            }
            .props-section:last-child {
                border-bottom: none;
            }
            .props-section-header {
                display: flex;
                align-items: center;
                gap: 6px;
                width: 100%;
                padding: 8px 14px;
                border: none;
                background: transparent;
                color: var(--text-dim);
                font-size: 11px;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.8px;
                text-align: left;
                cursor: pointer;
                box-sizing: border-box;
                user-select: none;
            }
            .props-section-header:hover {
                color: var(--text);
            }
            .props-section-header > svg {
                transition: transform 0.15s ease;
            }
            .props-section.open .props-section-header > svg {
                transform: rotate(90deg);
            }
            .props-section-title {
                flex: 1;
            }
            .props-row .props-row-x {
                opacity: 0;
                transition: opacity 0.12s ease;
            }
            .props-row:hover .props-row-x {
                opacity: 1;
            }
            .props-add-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 22px;
                height: 22px;
                padding: 0;
                border: none;
                border-radius: 6px;
                background: transparent;
                color: var(--text-dim);
                cursor: pointer;
                flex-shrink: 0;
                transition: background 0.15s ease, color 0.15s ease;
            }
            .props-add-btn:hover {
                background: rgba(0, 0, 0, 0.08);
                color: var(--text);
            }
            .props-add-btn.active {
                background: var(--accent);
                color: #fff;
            }
            .props-section-body {
                display: none;
                padding: 0 14px 12px;
            }
            .props-section.open .props-section-body {
                display: block;
            }

            /* Reparented panels: fill the narrow column */
            #props-panel .panel {
                width: auto;
                max-width: 100%;
                overflow: visible;
            }

            /* Standard vertical properties list (layers, slice groups) */
            .props-list {
                display: flex;
                flex-direction: column;
                gap: 2px;
                max-height: 180px;
                overflow-y: auto;
                padding: 2px;
            }
            .props-row {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 5px 8px;
                border-radius: 6px;
                cursor: pointer;
                font-size: 12px;
                color: var(--text);
                flex-shrink: 0;
                transition: background 0.12s ease;
            }
            .props-row:hover {
                background: rgba(0, 0, 0, 0.05);
            }
            .props-row.selected {
                background: var(--accent-tint);
                color: var(--accent-strong);
            }
            .props-row.selected:hover {
                background: var(--accent-tint-strong);
            }
            .props-swatch {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 20px;
                height: 20px;
                border-radius: 4px;
                font-size: 10px;
                font-weight: 600;
                flex-shrink: 0;
                box-sizing: border-box;
            }
            .props-row-label {
                flex: 1;
                min-width: 0;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
            .props-tag {
                font-size: 9px;
                font-weight: 600;
                padding: 1px 6px;
                border-radius: 7px;
                background: rgba(255, 170, 0, 0.22);
                color: #b47a00;
                flex-shrink: 0;
            }
            .props-row-x {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 18px;
                height: 18px;
                padding: 0;
                border: none;
                border-radius: 9px;
                background: transparent;
                color: #999;
                cursor: pointer;
                flex-shrink: 0;
            }
            .props-row-x:hover {
                background: rgba(0, 0, 0, 0.1);
                color: #333;
            }

            /* Options area under a properties list */
            .props-options {
                display: flex;
                flex-direction: column;
                gap: 8px;
                margin-top: 10px;
                padding-top: 10px;
                border-top: 1px solid rgba(0, 0, 0, 0.06);
            }
            .props-options-row {
                display: flex;
                align-items: center;
                gap: 4px;
                flex-wrap: wrap;
            }
            .props-field-label {
                color: var(--text-dim);
                font-size: 10px;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.6px;
            }
            .props-icon-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 30px;
                height: 30px;
                padding: 0;
                border: 1px solid transparent;
                border-radius: 6px;
                background: transparent;
                color: #555;
                cursor: pointer;
                flex-shrink: 0;
                transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
            }
            .props-icon-btn img {
                width: 22px;
                height: 22px;
                object-fit: contain;
                display: block;
                image-rendering: crisp-edges;
            }
            .props-icon-btn:hover {
                background: rgba(0, 0, 0, 0.06);
            }
            .props-icon-btn.active {
                background: var(--accent-tint);
                border-color: var(--accent);
                color: var(--accent-strong);
            }
            .props-hint {
                color: var(--text-dim);
                font-size: 11px;
                font-style: italic;
                padding: 4px 2px 2px;
            }

            #props-panel::-webkit-scrollbar,
            .props-list::-webkit-scrollbar {
                width: 8px;
            }
            #props-panel::-webkit-scrollbar-track,
            .props-list::-webkit-scrollbar-track {
                background: rgba(0, 0, 0, 0.05);
            }
            #props-panel::-webkit-scrollbar-thumb,
            .props-list::-webkit-scrollbar-thumb {
                background: rgba(0, 0, 0, 0.2);
                border-radius: 4px;
            }
            .panel, .props-list {
                scrollbar-width: thin;
                scrollbar-color: rgba(0, 0, 0, 0.2) rgba(0, 0, 0, 0.05);
            }

            /* Make mode: the MaterialsPanel stacks its content groups
               vertically inside the narrow panel (the first child is the
               Cut | 3D Print segmented control, which stays a row) */
            #props-make-content .materials-panel > div:not(:first-child) {
                flex-direction: column;
                gap: 14px;
            }
        `;
        document.head.appendChild(style);

        // Top-right preview panel container
        this.topRightContainer = document.createElement('div');
        this.topRightContainer.id = 'top-right-container';
        this.topRightContainer.style.cssText = `
            position: absolute;
            right: 12px;
            top: 80px;
            pointer-events: auto;
        `;
        this.uiContainer.appendChild(this.topRightContainer);
    }

    /**
     * Initialize UI panels
     */
    initializePanels() {
        // Toolbar (lives in the bottom dock)
        this.panels.tools = new ToolsPanel(this.app);
        this.panels.tools.onModeChange = (mode) => this.setMode(mode);
        this.bottomToolbar.appendChild(this.panels.tools.getElement());

        // Property panels (properties-panel sections, top to bottom)
        this.panels.camera = new CameraPanel(this.app);
        this.panels.physics = new PhysicsPanel(this.app);
        this.panels.layers = new LayersPanel(this.app);
        this.panels.slices = new SlicesPanel(this.app);

        this.createPropsSection('camera', 'Camera', this.panels.camera);
        this.createPropsSection('physics', 'Physics', this.panels.physics);
        this.createPropsSection('layers', 'Layers', this.panels.layers, {
            addTooltip: 'Add layer',
            onAdd: () => this.panels.layers.addLayer()
        });
        this.createPropsSection('slices', 'Slices', this.panels.slices, {
            addTooltip: 'Add slices...',
            onAdd: (btn) => this.openSliceAddMenu(btn),
            registerAddButton: (btn) => this.panels.slices.setHeaderAddButton(btn)
        });

        // Cross-chair curve (parametric modifier on the selected linked stroke)
        this.panels.strokeModifier = new StrokeModifierPanel(this.app);
        this.createPropsSection('crossCurve', 'Cross-chair curve', this.panels.strokeModifier);

        // Floating scene-settings card (',' shortcut; Escape closes).
        // Construction applies persisted settings to the live scene.
        this.panels.sceneSettings = new SceneSettingsPanel(this.app);

        this.buildSliceAddMenu();

        // Preview panel (always hidden for now; the Make split view
        // supersedes it)
        this.panels.preview = new PreviewPanel(this.app, this.topRightContainer);
        this.topRightContainer.style.display = 'none';

        // Fabrication panel (mounted into the properties panel only while
        // Make mode is on, so its isConnected checks keep gating the
        // split-screen overlay)
        this.panels.materials = new MaterialsPanel(this.app);

        // Properties panel visibility depends on mode + whether a design exists
        this.updateLeftPanelVisibility();

        console.log('UI panels initialized: bottom toolbar + right properties panel');
    }

    /**
     * Build one collapsible section of the properties panel and mount the
     * panel's existing element into its body (the panel keeps its own DOM
     * and logic; only widths/wrapping are overridden via CSS).
     * @param {Object} [opts]
     * @param {Function} [opts.onAdd] - Adds a small "+" button to the
     *   header (right-aligned) that calls this
     * @param {string} [opts.addTooltip]
     * @param {Function} [opts.registerAddButton] - Receives the "+" button
     *   element (so the owning panel can reflect armed/active state on it)
     */
    /**
     * Dark dropdown listing the slice types the + button can add
     */
    buildSliceAddMenu() {
        const menu = document.createElement('div');
        menu.id = 'slice-add-menu';
        menu.style.cssText = `
            position: fixed;
            display: none;
            background: rgba(30, 30, 32, 0.96);
            border-radius: 12px;
            box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
            padding: 6px;
            z-index: 3000;
            min-width: 190px;
        `;
        const items = [
            { label: 'Slice', icon: 'GUI_SLICE_SLICE_ICON.png', hint: 'click a shape', action: () => this.panels.slices.startSingleSlice() },
            { label: 'Slice Group', icon: 'GUI_SLICE_MULTISLICE_ICON.png', hint: 'start / end / density', action: () => this.panels.slices.startSliceGroup() },
            { label: 'Slat', icon: 'GUI_SLICE_SLAT_ICON.png', hint: '', action: () => this.panels.slices.startSingleSlat() },
            { label: 'Slat Group', icon: 'GUI_SLICE_SLAT_BUTTON.png', hint: '', action: () => this.panels.slices.startSlatGroup() }
        ];
        for (const item of items) {
            const row = document.createElement('div');
            row.style.cssText = `
                display: flex; align-items: center; gap: 10px;
                padding: 7px 10px; border-radius: 8px; cursor: pointer;
                color: #fff; font-size: 12px;
            `;
            const img = document.createElement('img');
            img.src = `/images/gui/${item.icon}`;
            img.style.cssText = 'width: 18px; height: 18px; object-fit: contain; filter: invert(1);';
            row.appendChild(img);
            const label = document.createElement('span');
            label.textContent = item.label;
            label.style.flex = '1';
            row.appendChild(label);
            if (item.hint) {
                const hint = document.createElement('span');
                hint.textContent = item.hint;
                hint.style.cssText = 'color: #999; font-size: 10px;';
                row.appendChild(hint);
            }
            row.addEventListener('mouseenter', () => row.style.background = 'rgba(255,255,255,0.12)');
            row.addEventListener('mouseleave', () => row.style.background = 'transparent');
            row.addEventListener('click', () => {
                this.closeSliceAddMenu();
                item.action();
            });
            menu.appendChild(row);
        }
        document.body.appendChild(menu);
        this.sliceAddMenu = menu;
        this._sliceMenuOutside = (e) => {
            if (!menu.contains(e.target)) this.closeSliceAddMenu();
        };
    }

    openSliceAddMenu(anchorBtn) {
        const menu = this.sliceAddMenu;
        if (!menu) return;
        if (menu.style.display === 'block') { this.closeSliceAddMenu(); return; }
        const r = anchorBtn.getBoundingClientRect();
        menu.style.display = 'block';
        // Left of the button (panel hugs the right edge)
        menu.style.top = `${r.bottom + 6}px`;
        menu.style.left = `${Math.max(8, r.right - menu.offsetWidth)}px`;
        setTimeout(() => document.addEventListener('pointerdown', this._sliceMenuOutside, true), 0);
    }

    closeSliceAddMenu() {
        if (this.sliceAddMenu) this.sliceAddMenu.style.display = 'none';
        document.removeEventListener('pointerdown', this._sliceMenuOutside, true);
    }

    createPropsSection(id, title, panel, opts = {}) {
        const section = document.createElement('div');
        section.className = 'props-section';
        if (this.sectionOpen[id]) section.classList.add('open');

        // Header is a div (not a button) so the "+" can be a real button
        const header = document.createElement('div');
        header.className = 'props-section-header';
        header.appendChild(phIcon(ICONS.caretRight, 10));

        const label = document.createElement('span');
        label.className = 'props-section-title';
        label.textContent = title;
        header.appendChild(label);

        if (opts.onAdd) {
            const addBtn = document.createElement('button');
            addBtn.className = 'props-add-btn';
            addBtn.title = opts.addTooltip || `Add ${title.toLowerCase()}`;
            addBtn.appendChild(phIcon(ICONS.plus, 16));
            addBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                // Adding implies the section should be visible
                if (!this.sectionOpen[id]) this.toggleSection(id);
                opts.onAdd(addBtn);
            });
            header.appendChild(addBtn);
            if (opts.registerAddButton) opts.registerAddButton(addBtn);
        }

        section.appendChild(header);

        const body = document.createElement('div');
        body.className = 'props-section-body';
        body.appendChild(panel.getElement());
        section.appendChild(body);

        header.addEventListener('click', () => this.toggleSection(id));

        this.designContainer.appendChild(section);
        this.sections[id] = { section, body, panel };
        return section;
    }

    /**
     * Toggle one collapsible section open/closed
     */
    toggleSection(id) {
        const entry = this.sections[id];
        if (!entry) return;
        this.sectionOpen[id] = !this.sectionOpen[id];
        entry.section.classList.toggle('open', this.sectionOpen[id]);
        // Refresh the panel when revealed
        if (this.sectionOpen[id] && entry.panel.update) {
            entry.panel.update();
        }
    }

    /**
     * Switch between Design and Make mode.
     * Make mode: the properties panel swaps to the MaterialsPanel content,
     * the split-screen cut overlay comes up (with its camera shift) and the
     * toolbar collapses to its Make subset. Design mode restores all three.
     * @param {string} mode - 'design' | 'make'
     */
    setMode(mode) {
        if (mode !== 'design' && mode !== 'make') return;
        if (this.mode === mode) return;
        this.mode = mode;

        const materials = this.panels.materials;

        // The sketch overlay (centre lines, control points - all children
        // of app.overlayFrame, so they inherit its visibility) is a Design
        // mode aid; hide it for ALL of Make mode
        if (this.app.overlayFrame) {
            this.app.overlayFrame.visible = mode !== 'make';
        }

        if (mode === 'make') {
            this.designContainer.style.display = 'none';
            this.makeContainer.appendChild(materials.getElement());
            this.makeContainer.style.display = 'flex';
            this.updateLeftPanelVisibility();
            // Now that the panel is connected, this shows the split-screen
            // overlay (with camera shift) and refreshes the pattern
            if (materials.update) materials.update();
        } else {
            this.makeContainer.style.display = 'none';
            const el = materials.getElement();
            if (el.parentNode === this.makeContainer) {
                this.makeContainer.removeChild(el);
            }
            this.designContainer.style.display = 'flex';
            // Force the split-screen preview off (also undoes the camera shift)
            materials.setSplitScreen?.(false);
            this.updateLeftPanelVisibility();
        }

        // Toolbar subset + toggle visuals
        this.panels.tools.setMode?.(mode);
    }

    /**
     * Show/hide the properties panel.
     * Design mode: only while a design exists (called from Application's
     * onShapeChange chain - covers drawing, delete, clearAll, undo/redo and
     * design load). Make mode: always shown (an empty pattern is fine).
     */
    updateLeftPanelVisibility() {
        const sp = this.app.sketchPlane;
        const hasDesign = !!sp && (
            (sp.closedShapes && sp.closedShapes.length > 0) ||
            (sp.splines && sp.splines.length > 0)
        );
        // Auto-show (chair exists / make mode), overridden while the user
        // has explicitly hidden the panel via the chevron tab
        const autoShow = this.mode === 'make' || hasDesign;
        const show = autoShow && !this.userHiddenProps;
        this.propsPanel.style.display = show ? 'flex' : 'none';

        // The chevron tab hugs the panel's left edge while shown, and
        // parks at the screen edge while hidden (still clickable)
        if (this.propsToggleTab) {
            this.propsToggleTab.style.display = autoShow ? 'flex' : 'none';
            if (show) {
                this.propsToggleTab.style.right = 'calc(16px + 264px)';
                this.propsToggleTab.title = 'Hide panel';
                this.propsToggleTab.replaceChildren(phIcon(ICONS.caretRight, 14));
            } else {
                this.propsToggleTab.style.right = '0px';
                this.propsToggleTab.title = 'Show panel';
                this.propsToggleTab.replaceChildren(phIcon(ICONS.caretLeft, 14));
            }
        }
    }

    /**
     * Update UI based on application state
     */
    update() {
        // Update panels
        Object.values(this.panels).forEach(panel => {
            if (panel.update) {
                panel.update();
            }
        });

        this.updateLeftPanelVisibility();
    }

    /**
     * Show/hide UI
     */
    setVisible(visible) {
        this.uiContainer.style.display = visible ? 'block' : 'none';
    }

    /**
     * Cleanup
     */
    dispose() {
        Object.values(this.panels).forEach(panel => {
            if (panel.dispose) {
                panel.dispose();
            }
        });

        if (this.uiContainer.parentNode) {
            this.uiContainer.parentNode.removeChild(this.uiContainer);
        }
    }
}
